import { normalizePublicMediaUrl } from "./mediaUrl";

type CarouselItem = { url?: string; type?: string; order?: number };

/**
 * Meta (Instagram + Facebook) carousel publish prep.
 *
 * - Instagram: any IMAGE/VIDEO mix supported. With >=2 items, sets post_type
 *   CAROUSEL, normalizes URLs, and aligns image_url to the first slide.
 * - Facebook: IMAGE-only. Drops any VIDEO entries first; only sets post_type
 *   CAROUSEL when >=2 IMAGE entries remain.
 *
 * Other platforms: only normalize URLs of any existing carousel_media.
 */
export function applyMetaCarouselToPublishPayload(
  payload: Record<string, unknown>
): void {
  const platformUpper = String(payload.platform ?? "").toUpperCase();
  const isInstagram =
    platformUpper.includes("INSTAGRAM") || platformUpper === "INSTAGRAM_REELS";
  const isFacebook = platformUpper === "FACEBOOK";
  const raw = payload.carousel_media;
  const cm: CarouselItem[] = Array.isArray(raw) ? (raw as CarouselItem[]) : [];

  if (isInstagram && cm.length >= 2) {
    payload.post_type = "CAROUSEL";
    const normalizedCarousel = cm.map((item) => ({
      ...item,
      url: normalizePublicMediaUrl(item.url ?? ""),
    }));
    payload.carousel_media = normalizedCarousel;
    const firstUrl = normalizedCarousel[0]?.url;
    if (typeof firstUrl === "string" && firstUrl) {
      payload.image_url = firstUrl;
    }
  } else if (isFacebook && cm.length > 0) {
    // FB multi-photo: drop VIDEO entries, only mark CAROUSEL when >=2 images remain.
    const imagesOnly = cm
      .filter(
        (item) => String(item.type ?? "").toUpperCase() !== "VIDEO"
      )
      .map((item) => ({
        ...item,
        url: normalizePublicMediaUrl(item.url ?? ""),
      }));
    payload.carousel_media = imagesOnly;
    if (imagesOnly.length >= 2) {
      payload.post_type = "CAROUSEL";
      const firstUrl = imagesOnly[0]?.url;
      if (typeof firstUrl === "string" && firstUrl) {
        payload.image_url = firstUrl;
      }
    }
  } else if (cm.length > 0) {
    payload.carousel_media = cm.map((item) => ({
      ...item,
      url: normalizePublicMediaUrl(item.url ?? ""),
    }));
  }
}

/** @deprecated Use applyMetaCarouselToPublishPayload. */
export const applyInstagramCarouselToPublishPayload =
  applyMetaCarouselToPublishPayload;
